import React, { useState, useRef, forwardRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { styles, AccordionFilters, Icon, mapPlaylistFromVideoSearch, LoadingSpinner, Button, filterPlaylistFromParams } from 'yankees-react-utils';
import { NavLink, Route, useHistory } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import MyPlaylists from './MyPlaylists';
import VideoPlayerAdvanced from '../../components/VideoPlayerAdvanced';
import { chatamsBlue } from '../../styles/theme';
import PlayerSearch from '../../components/PlayerSearch';
import TeamSearch from '../../components/TeamSearch';
import useConfig from '../../utils/useConfig';
import { Video } from '../../data';
import usePlayerSelector from '../../utils/usePlayerSelector';
import SavedSearchModal from './SavedSearchModal';
import SavedSearches from './SavedSearches';
import { store } from '../../store';

const StyledInGameReport = styled.div`
    margin: -${styles.theme.margin.body};
    height: calc(100% + 2 * ${styles.theme.margin.body});
    display: flex;
    flex-direction: column;
    position: relative;
`;
const StyledInGameReportSelector = styled.div`
    flex-grow: 0;
    width: ${(props) => (props.isOpen ? '280' : '45')}px;
    min-width: ${(props) => (props.isOpen ? '280' : '45')}px;
    border-right: 1px solid ${styles.theme.border.alto};
    background-color: white;
    a.h5, form {
        display: block;
        padding-left: 15px;
        color: ${chatamsBlue};
        background-color: white;
        padding: 7.5px 10px 7.5px;
        margin: 0;
    }
    a.h5:not(.video-search) {
        border-bottom: 1px solid ${styles.theme.border.alto};
    }
    a.active {
        &.h5, &.h5:hover {
            background-color: #CCE5FF;
        }
    }
    z-index: 1000;
    overflow-y:auto;
    display: flex;
    flex-direction: column;
    transition: all 0.35s;

    ${(props) => (props.isOpen ? '' : 'justify-content: space-between;')}
`;
const StyledInGameReportContent = styled.div`
    flex-grow: 1;
    padding: ${styles.theme.margin.body};
`;
const StyledInGameReportContainer = styled.div`
    flex-grow: 1;
    display: flex;
    overflow-y: hidden;
    > ${StyledInGameReportContent} {
        overflow-y: auto;
    }
`;
const FilterTitle = styled.div`
    width: 19px;
    transform: rotate(270deg);
    font-size: 14px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
    margin-top: 105px;
`;
const FilterIcon = styled(Icon)`
    flex-shrink: 0;
    width: 16px;
    margin-left: 10px;
`;

const VideoSearch = forwardRef(({ isSearching, playlist, innerRef, isMenuOpen, ...rest }, ref) => {
    const { t: translate } = useTranslation();

    if (isSearching) {
        return (
            <div className="d-flex justify-content-center py-5">
                <LoadingSpinner />
            </div>
        );
    }

    return (

        <div>
            { playlist
                ? (
                    <VideoPlayerAdvanced
                        playlist={playlist || []}
                        hideDownload
                        hideShare
                        hideFrameGrab
                        roundCorners
                        ref={ref}
                        isLargeView={!isMenuOpen}
                        hideFilter
                        {...rest}
                    />
                )
                : <div className="d-flex justify-content-center py-5 text-muted">{translate('sections.video-playlists.use-the-fields')}</div>}
        </div>
    );
});

const prefix = '/video-playlists/';

const VideoPlaylists = ({ location }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const [isSearching, setIsSearching] = useState(false);
    const [playlist, setPlaylist] = useState([]);
    const [filteredPlaylist, setFilteredPlaylist] = useState(null);
    const [filter, setFilter] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [showSearch, setShowSearch] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(true);
    const [hasSearched, setHasSearched] = useState(false);
    const [showSavedSearchModal, setShowSavedSearchModal] = useState(false);
    const [searchForm, setSearchForm] = useState({});
    const [canSaveSearch, setCanSaveSearch] = useState(false);

    const videoPlayerRef = useRef();
    const modalContainer = useRef();
    const accordionFilterRef = useRef();

    const { CurrentYear } = useConfig();
    const player = usePlayerSelector();
    const defaultForm = player ? {
        [player.IsPitcher ? 'Pitcher' : 'Batter']: player,
        LevelID: (player.Team?.Level?.LevelID ?? 8) === 8 ? 8 : 7,
    } : {};

    const history = useHistory();

    useEffect(() => {
        const unlisten = history.listen(() => {
            setShowSearch(window.location.pathname === '/video-playlists/video-search');
        });

        return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { authState: { accessToken } } = useOktaAuth();
    let abortController = null;

    const onVideoSearch = async (form, _filter = {}, savedSearch = null, _isMenuOpen = false, _canSaveSearch = true) => {
        history.push(`${prefix}video-search`);

        try {
            if (abortController) {
                abortController.abort();
            }
            abortController = new AbortController();
            if (form.BaseEvent) {
                form.IsLastPitch = true;
            }
            setIsSearching(true);
            setIsMenuOpen(_isMenuOpen);
            setCanSaveSearch(_canSaveSearch);

            if (savedSearch) {
                accordionFilterRef.current.setSearchForm(savedSearch);
            }
            setSearchForm(form);

            const resp = await Video.search(accessToken, {
                ...form,
                Pitcher: undefined,
                Batter: undefined,
                Catcher: undefined,
                PitchingTeam: undefined,
                BattingTeam: undefined,
                Type: undefined,
                IsLHP: form.Throws === null || form.Throws === undefined ? undefined : !!form.Throws,
                IsLHB: form.Bats === null || form.Bats === undefined ? undefined : !!form.Bats,
            }, { signal: abortController.signal });
            const p = mapPlaylistFromVideoSearch(resp, user.Roles.map((r) => r.RoleName));
            setPlaylist(p);
            setFilteredPlaylist(filterPlaylistFromParams(p, _filter));
            setFilter(_filter);
            setShowFilters(true);
            setHasSearched(true);
        } catch (error) {
            if (error.name !== 'AbortError') {
                throw error;
            }
        } finally {
            setIsSearching(false);
        }
        return [];
    };

    const updatePlaylist = (p, f) => {
        const fp = filterPlaylistFromParams(p, f);
        setFilter(f);
        videoPlayerRef.current.setFilteredVideos(fp, f);
        setCanSaveSearch(true);
    };

    const onVideoClick = async (savedSearch) => {
        const newFilter = JSON.parse(savedSearch.Filters || '{}') || {};

        const cleanSavedSearch = {
            ...savedSearch,
            IsLHP: savedSearch.Throws !== null ? savedSearch.Throws === 1 : null,
            IsLHB: savedSearch.Bats !== null ? savedSearch.Bats === 1 : null,
        };
        await history.push(`${prefix}video-search`);
        await onVideoSearch(cleanSavedSearch, newFilter, cleanSavedSearch, true, false);

        accordionFilterRef.current.setFilter(newFilter);
        accordionFilterRef.current.setIsEditingInitSearch(false);
    };

    const pages = [
        {
            title: translate('sections.video-playlists.my-playlists'),
            route: 'my-playlists',
            render: () => <MyPlaylists />,
        },
        {
            title: translate('sections.video-playlists.saved-searches'),
            route: 'saved-searches',
            render: () => <SavedSearches onVideoClick={onVideoClick} />,
        },
    ];

    return (
        <StyledInGameReport ref={modalContainer}>
            <StyledInGameReportContainer>
                <StyledInGameReportSelector isOpen={isMenuOpen}>
                    {isMenuOpen && (
                        <div className="d-inline-flex justify-content-end border-bottom">
                            <Button variant="link" style={{ paddingRight: '4px' }}>
                                <Icon icon="nyy-cross" color={styles.theme.fontColors.dodgerBlue} onClick={() => setIsMenuOpen(false)} size="2.5em" />
                            </Button>
                        </div>
                    ) }
                    <div className={`${isMenuOpen ? 'd-inline-flex' : 'd-none'} flex-column`}>
                        {pages.map(({ title, route }) => (
                            <NavLink className="h5" key={route} to={`${prefix}${route}`}>{title}</NavLink>
                        ))}
                        <AccordionFilters
                            yearId={CurrentYear}
                            playerSearch={PlayerSearch}
                            teamSearch={TeamSearch}
                            initForm={defaultForm}
                            hideSavedSearches
                            showClear
                            searchButtonTitle={translate('common.search')}
                            onSubmit={onVideoSearch}
                            playlist={playlist}
                            filter={filter}
                            onFilter={(p, f) => updatePlaylist(p, f)}
                            showFilters={showFilters}
                            showSearch={showSearch}
                            modalContainer={modalContainer}
                            showCancel={hasSearched}
                            title={<NavLink className="h5" to={`${prefix}video-search`}>{translate('sections.video-playlists.video-search')}</NavLink>}
                            goToSearch={() => history.push(`${prefix}video-search`)}
                            ref={accordionFilterRef}
                            hideAmateurLevels
                        />
                    </div>

                    {!isMenuOpen
                    && (
                        <Button
                            variant="link"
                            className={`${isMenuOpen ? 'd-none' : ''} text-dark h-100 d-flex align-items-start`}
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            <FilterTitle>
                                {translate('sections.video-playlists.search-filter')}
                                <FilterIcon icon="chevron-down" size="2.5em" color={styles.theme.fontColors.dodgerBlue} />
                            </FilterTitle>
                        </Button>
                    ) }

                </StyledInGameReportSelector>
                <StyledInGameReportContent>
                    {pages.map(({ route, render }) => (
                        <Route key={route} path={`${prefix}${route}`} render={render} location={location} />
                    ))}
                    <Route path={`${prefix}video-search`}>
                        <div className="p-2">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="mb-0">{translate('sections.video-playlists.video-search')}</h3>
                                {hasSearched && <Button variant="primary" disabled={!canSaveSearch} onClick={() => setShowSavedSearchModal(true)}>{translate('sections.video-playlists.save-search')}</Button>}
                            </div>
                            <VideoSearch
                                isSearching={isSearching}
                                playlist={filteredPlaylist}
                                location={location}
                                ref={videoPlayerRef}
                                isMenuOpen={isMenuOpen}
                            />
                        </div>
                    </Route>
                </StyledInGameReportContent>
            </StyledInGameReportContainer>
            <SavedSearchModal
                showModal={showSavedSearchModal}
                setShowModal={setShowSavedSearchModal}
                modalContainer={modalContainer}
                searchForm={searchForm}
                filter={filter}
                onCreate={() => setCanSaveSearch(false)}
            />
        </StyledInGameReport>
    );
};

export default VideoPlaylists;
